import { useEffect, useState } from "react";
import { Tabs } from "@fawkes/ui";
import { useTranslate } from "@refinedev/core";

import { CommTemplateList } from "./communicatie/CommTemplateList";
import { CommTemplateDetail } from "./communicatie/CommTemplateDetail";
import { useImportTaak } from "./communicatie/editor/importTaak";
import { CommMediaList } from "./communicatie/CommMediaList";
import "./communicatie/CommunicatieReadView.css";

// communicatie: de SPA-schermen over de communicatie-endpoints
//   • GET /api/communicatie/templates (+ /{id}, /{id}/versions)
//   • GET /api/communicatie/media
// Twee tabbladen: Sjablonen en Media. Het logtabblad is eruit — het verzendlogboek hoort bij het
// dossier waar het over gaat, niet als losse lijst naast de sjablonen; het endpoint blijft.
// Het sjabloontabblad klapt in dezelfde plek door naar de editor.
//
// COEXISTENCE: this is wired ADDITIVELY as the `communicatie-react` SPA page; the legacy Jmix
// `communicatie` / `communicatie-log` menu entries stay reachable (strangler-fig). DISTINCT from the
// settings-tier `CommunicatieScreen` (contracten/afdrukken/email-berichten over report_template) —
// that is the Settings tile, a different subsystem.
//
// NOTE: every fetch url is RELATIVE — the dataProvider prepends `/api` once (a leading `/` would
// double to /api/api and 404).

type Tab = "templates" | "media";

export function CommunicatieReadView() {
  const t = useTranslate();
  const [activeTab, setActiveTab] = useState<Tab>("templates");
  // The template tab is a list ⇄ detail master/detail; the selected template id drives the drill-in.
  const [templateId, setTemplateId] = useState<string | null>(null);
  // "Bekijken" in de melding van een nagebouwd document: open het sjabloon waarvoor het was. De editor
  // opent daar zelf het venster met het resultaat.
  const importStand = useImportTaak();
  useEffect(() => {
    if ((importStand.status === "klaar" || importStand.status === "fout") && importStand.bekijken) {
      setTemplateId(importStand.templateId);
    }
  }, [importStand]);

  const tabs: { key: Tab; label: string }[] = [
    { key: "templates", label: t("communicatieRead.tab.templates") },
    { key: "media", label: t("communicatieRead.tab.media") },
  ];

  // Een geopend sjabloon vult het scherm zelf; de tabstrook eromheen is dan alleen nog een knop
  // waarmee je je werk weggooit.
  const inEditor = activeTab === "templates" && templateId !== null;

  return (
    <div className={`comm-read-shell${inEditor ? " comm-read-shell--editor" : ""}`}>
      {!inEditor && (
        <div className="comm-read-tab-row">
          <Tabs
            tabs={tabs}
            activeKey={activeTab}
            ariaLabel={t("communicatieRead.title")}
            onChange={(key) => {
              setActiveTab(key as Tab);
              // Leaving the template tab drops any drill-in so we return to the list next time.
              if (key !== "templates") setTemplateId(null);
            }}
          />
        </div>
      )}

      {activeTab === "templates" &&
        (templateId ? (
          <CommTemplateDetail id={templateId} onBack={() => setTemplateId(null)} />
        ) : (
          <CommTemplateList onOpen={setTemplateId} />
        ))}

      {activeTab === "media" && <CommMediaList />}
    </div>
  );
}
